import { useEffect, useState } from 'react';

/**
 * Per-table persisted preferences (column order, visibility, sizes, page size,
 * density). Keyed by a stable `tableId` so different tables don't trample
 * each other's settings.
 */
export interface TablePrefs {
  columnOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  columnSizing?: Record<string, number>;
  pageSize?: number;
  density?: 'compact' | 'comfortable' | 'spacious';
}

const STORAGE_PREFIX = 'obd3:table:';

function readPrefs(tableId: string): TablePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tableId);
    return raw ? (JSON.parse(raw) as TablePrefs) : {};
  } catch {
    return {};
  }
}

function writePrefs(tableId: string, prefs: TablePrefs): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + tableId, JSON.stringify(prefs));
  } catch {
    /* localStorage may be unavailable in private mode — silently drop. */
  }
}

export function useTablePrefs(tableId: string): [TablePrefs, (next: TablePrefs) => void] {
  const [prefs, setPrefs] = useState<TablePrefs>(() => readPrefs(tableId));

  useEffect(() => {
    writePrefs(tableId, prefs);
  }, [tableId, prefs]);

  return [prefs, setPrefs];
}
